import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { Mockup, MockupWidget } from './types';

export const mockupApiRef = createApiRef<MockupApi>({
    id: 'plugin.mockup.service',
});

export interface MockupApi {
    listMockups(owner?: string): Promise<Mockup[]>;
    createMockup(name: string, owner: string, description?: string): Promise<Mockup>;
    updateMockup(id: string, updates: { name?: string; description?: string }): Promise<Mockup>;
    deleteMockup(id: string): Promise<void>;
    getMockup(id: string): Promise<Mockup & { latestVersion?: { layoutData: MockupWidget[], versionTag: string } }>;
    saveMockupVersion(mockupId: string, versionTag: string, layoutData: MockupWidget[]): Promise<void>;
    getMockupVersions(mockupId: string): Promise<{ id: string, versionTag: string, createdAt: string }[]>;
    getMockupVersion(mockupId: string, versionId: string): Promise<{ layoutData: MockupWidget[] }>;
}

export class MockupClient implements MockupApi {
    private readonly discoveryApi: DiscoveryApi;
    private readonly fetchApi: FetchApi;

    constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
        this.discoveryApi = options.discoveryApi;
        this.fetchApi = options.fetchApi;
    }

    private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
        const baseUrl = await this.discoveryApi.getBaseUrl('mockup');
        const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);

        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
        }

        return response.json();
    }

    async listMockups(owner?: string): Promise<Mockup[]> {
        const query = owner ? `?owner=${encodeURIComponent(owner)}` : '';
        return this.fetch<Mockup[]>(`/mockups${query}`);
    }

    async createMockup(name: string, owner: string, description?: string): Promise<Mockup> {
        return this.fetch<Mockup>('/mockups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, owner, description }),
        });
    }

    async updateMockup(id: string, updates: { name?: string; description?: string }): Promise<Mockup> {
        return this.fetch<Mockup>(`/mockups/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates),
        });
    }

    async deleteMockup(id: string): Promise<void> {
        await this.fetch(`/mockups/${id}`, {
            method: 'DELETE',
        });
    }

    async getMockup(id: string): Promise<Mockup & { latestVersion?: { layoutData: MockupWidget[], versionTag: string } }> {
        return this.fetch(`/mockups/${id}`);
    }

    async saveMockupVersion(mockupId: string, versionTag: string, layoutData: MockupWidget[]): Promise<void> {
        await this.fetch(`/mockups/${mockupId}/versions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ versionTag, layoutData }),
        });
    }

    async getMockupVersions(mockupId: string): Promise<{ id: string, versionTag: string, createdAt: string }[]> {
        return this.fetch(`/mockups/${mockupId}/versions`);
    }

    async getMockupVersion(mockupId: string, versionId: string): Promise<{ layoutData: MockupWidget[] }> {
        return this.fetch(`/mockups/${mockupId}/versions/${versionId}`);
    }
}
